/**
 * Softphone / media-setup failure helpers for Amazon Connect Streams.
 * Classifies SDK softphone errors and checks mic permission before accept.
 */

import {
    buildConnectionFailureMessage,
    getConnectionFailureGuideline,
    normalizeConnectSoftphoneErrorType,
    type ConnectSoftphoneErrorType,
    type ConnectionFailureStep,
} from './connectionFailureGuidelines';

export type { ConnectSoftphoneErrorType, ConnectionFailureStep };

export type SoftphoneFailureKind =
    | 'microphone'
    | 'ice'
    | 'webrtc'
    | 'busy'
    | 'pending_busy'
    | 'contact'
    | 'other';

export interface SoftphoneFailureNotice {
    kind: SoftphoneFailureKind;
    errorType: ConnectSoftphoneErrorType;
    title: string;
    typicalCause: string;
    summary: string;
    steps: ConnectionFailureStep[];
    /** Combined copy for compact banners/toasts. */
    message: string;
    /** @deprecated Use typicalCause */
    issue: string;
    /** @deprecated Use steps */
    solution: string;
    at: number;
    source: string;
}

export type MicPermissionState = 'granted' | 'denied' | 'prompt' | 'unknown';

export function isMicrophoneUnavailable(state: MicPermissionState): boolean {
    return state === 'denied';
}

export function extractSoftphoneError(err: unknown): { errorType: string; message: string } {
    if (err == null) return { errorType: 'other', message: 'Unknown softphone error' };
    if (typeof err !== 'object') {
        return { errorType: 'other', message: String(err) };
    }
    const e = err as Record<string, any>;
    const errorType = String(
        (typeof e.getErrorType === 'function' ? e.getErrorType() : null) ||
            e.errorType ||
            e.type ||
            e.name ||
            'other',
    ).toLowerCase();
    const message = String(
        (typeof e.getErrorMessage === 'function' ? e.getErrorMessage() : null) ||
            e.errorMessage ||
            e.message ||
            'Softphone media setup failed',
    );
    return { errorType, message };
}

function kindFromErrorType(errorType: ConnectSoftphoneErrorType): SoftphoneFailureKind {
    switch (errorType) {
        case 'microphone_not_shared':
            return 'microphone';
        case 'ice_collection_timeout':
            return 'ice';
        case 'user_busy_error':
            return 'busy';
        case 'webrtc_error':
        case 'signalling_handshake_failure':
        case 'signalling_connection_failure':
        case 'realtime_communication_error':
            return 'webrtc';
        default:
            return 'other';
    }
}

export function classifySoftphoneFailure(
    err: unknown,
    source: string,
): SoftphoneFailureNotice {
    const { errorType: rawErrorType } = extractSoftphoneError(err);
    const errorType = normalizeConnectSoftphoneErrorType(rawErrorType);
    const guideline = getConnectionFailureGuideline(errorType);
    const at = Date.now();
    const solution = guideline.steps.map((step) => step.text).join(' ');

    return {
        kind: kindFromErrorType(errorType),
        errorType,
        title: guideline.label,
        typicalCause: guideline.typicalCause,
        summary: guideline.summary,
        steps: guideline.steps,
        message: buildConnectionFailureMessage(guideline),
        issue: guideline.summary,
        solution,
        at,
        source,
    };
}

export async function queryMicPermission(): Promise<MicPermissionState> {
    try {
        const perms = navigator.permissions;
        if (perms?.query) {
            const result = await perms.query({ name: 'microphone' as PermissionDescriptor['name'] });
            if (result.state === 'granted' || result.state === 'denied' || result.state === 'prompt') {
                return result.state;
            }
        }
    } catch {
        /* Permissions-Policy / Firefox name quirks */
    }
    return 'unknown';
}

/** Prompt for mic if needed. Returns true when audio capture succeeds. */
export async function ensureMicAccess(): Promise<boolean> {
    if (!navigator.mediaDevices?.getUserMedia) return false;
    try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
        return true;
    } catch {
        return false;
    }
}
